import React, { useState, useEffect, useRef } from "react";
import { Bell } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useNotifications, notifTarget } from "../context/NotificationContext";

const NotificationItem = ({ n, index, target, onOpen }) => (
  <div
    onClick={() => { if (target) onOpen(target); }}
    className={`p-3 border-b border-[#EBF4F6] text-sm ${n.read ? "opacity-60" : ""} ${target ? "cursor-pointer hover:bg-[#EBF4F6] transition-colors" : ""}`}
    data-testid={`notif-item-${index}`}
  >
    <div className="font-bold text-[#09637E] break-words">{n.title}</div>
    <div className="text-[#333333] mt-1 break-words whitespace-pre-wrap leading-6">{n.message}</div>
    <div className="flex items-center justify-between mt-1">
      {n.ts ? <div className="text-xs text-[#666666]">{new Date(n.ts).toLocaleString("ar-SY")}</div> : <span />}
      {target && <span className="text-[10px] font-bold text-[#088395]">اضغط للانتقال ←</span>}
    </div>
  </div>
);

const NotificationPanel = ({ items, role, onClose, onOpen }) => (
  <>
    {/* Backdrop on mobile for tap-to-close */}
    <div className="fixed inset-0 bg-black/20 z-[90] md:hidden" onClick={onClose} />
    <div
      className="fixed top-16 md:top-20 inset-x-2 md:inset-x-auto md:right-[16.5rem] md:w-96 max-h-[70vh] overflow-y-auto bg-white border border-[#7AB2B2] rounded-xl shadow-2xl z-[100]"
      data-testid="notification-panel"
    >
      <div className="sticky top-0 bg-white p-3 border-b border-[#EBF4F6] font-bold text-[#333333] flex items-center justify-between">
        <span>الإشعارات</span>
        <button onClick={onClose} className="md:hidden text-[#666666] text-lg leading-none px-2" aria-label="إغلاق">✕</button>
      </div>
      {items.length === 0 ? (
        <div className="p-6 text-center text-[#666666] text-sm">لا توجد إشعارات</div>
      ) : items.map((n, i) => (
        <NotificationItem key={n.id || `notif-${i}`} n={n} index={i} target={notifTarget(n, role)} onOpen={onOpen} />
      ))}
    </div>
  </>
);

const NotificationBell = () => {
  const { items, unread, markAllRead } = useNotifications();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && unread > 0) markAllRead();
  };

  const openTarget = (target) => { setOpen(false); navigate(target); };

  // Close on outside click / Esc key
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onEsc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  return (
    <div className="relative" data-testid="notification-bell" ref={wrapRef}>
      <button onClick={toggle} className="relative w-10 h-10 rounded-lg hover:bg-[#EBF4F6] flex items-center justify-center text-[#09637E]" aria-label="الإشعارات">
        <Bell size={20} />
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center" data-testid="notification-badge">
            {unread > 9 ? "+9" : unread}
          </span>
        )}
      </button>
      {open && <NotificationPanel items={items} role={user?.role} onClose={() => setOpen(false)} onOpen={openTarget} />}
    </div>
  );
};

export default NotificationBell;
